import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Button } from '../button'
import { Spinner } from '../spinner'
import { PhoneNumberInput, isValidPhone } from '../phone-number-input'
import {
  contactQueries,
  contactSearchQueries,
  whatsappTemplateQueries,
} from 'src/services/whatsapp/queries'
import { Contact, ContactSearchResult } from 'src/services/whatsapp'
import { BindingContext } from 'src/services/whatsapp/bindings'
import { useTemplateSendValues } from 'src/services/whatsapp/use-template-bindings'
import { numberQueries } from 'src/services/numbers'
import { useAuth } from 'src/stores/auth'
import { XMarkIcon, MagnifyingGlassIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useDebouncedValue } from 'src/hooks/use-debounced-value'
import { format } from 'date-fns'
import { cn } from 'src/utils/style'
import { isInsufficientBalanceError } from 'src/utils/errors'
import { NoApprovedTemplateNotice } from './no-approved-template-notice'
import { TemplateVariablePrompts } from './template-variable-prompts'
import { groupTemplatesByName, renderBody, TemplateGroup } from './template-composer'
import { InfoHint } from 'src/components/info-hint'

interface StartConversationOptions {
  reservationId?: string
  contactName?: string
  contactLanguage?: string
  /** Approved template the opening message is sent with. First contact is
   * business-initiated, so there is no free-text alternative here. */
  openingTemplateName?: string
  openingTemplateLanguage?: string
  /** Values the sender typed for variables that don't resolve on their own. */
  openingTemplateVariables?: Record<string, string>
  /** Sendable number of the selected org the conversation is opened from. Pins
   * the sending org to the chat selection instead of a global default. */
  ourNumber?: string
}

interface NewConversationPanelProps {
  onClose: () => void
  onSubmit: (contactNumber: string, options?: StartConversationOptions) => void
  isLoading?: boolean
  /** The opening send's failure, if any. Classified here into plain copy (and a
   * balance top-up link) — the raw API/provider detail is never rendered. */
  error?: Error | null
  /** The org the chat is scoped to (chosen in the card selector). All of the
   * panel's reads — numbers, templates, contacts — and the opening send are
   * scoped to this org, not the global active org. */
  selectedOrgId: string | null
  /** Switches the page's active tab to Templates. Templates lives as a tab on
   * this same page now, not a separate route. */
  onReviewTemplates: () => void
}

// Language options offered when naming a brand-new contact. Blank = let the
// backend seed the language from the organization's country (its label is
// translated at render via `chat.languageAuto`). The remaining labels are
// language endonyms shown in their own language, so they are not translated.
const CONTACT_LANGUAGE_OPTIONS = [
  { value: '', label: '' },
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
]

function formatReservationSummary(
  reservation: ContactSearchResult['reservation'],
  t: (key: string) => string,
): string | null {
  if (!reservation) return null
  const parts: string[] = []
  if (reservation.providerConfirmationCode) parts.push(reservation.providerConfirmationCode)
  if (reservation.checkinDate) {
    const checkin = format(new Date(reservation.checkinDate), 'MMM d')
    const checkout = reservation.checkoutDate
      ? format(new Date(reservation.checkoutDate), 'MMM d')
      : t('chat.tbd')
    parts.push(`${checkin} – ${checkout}`)
  }
  return parts.join(' · ') || null
}

/**
 * Order the approved templates for the picker: the platform default first, then
 * those approved in the recipient's language, then by name.
 */
function orderForRecipient(groups: TemplateGroup[], language: string | null): TemplateGroup[] {
  const inLanguage = (group: TemplateGroup) => Boolean(language && group.byLanguage[language])
  return [...groups].sort(
    (a, b) =>
      Number(b.isDefault) - Number(a.isDefault) ||
      Number(inLanguage(b)) - Number(inLanguage(a)) ||
      a.name.localeCompare(b.name),
  )
}

export const NewConversationPanel = ({
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
  selectedOrgId,
  onReviewTemplates,
}: NewConversationPanelProps) => {
  const { t } = useTranslation()
  const user = useAuth.use.user()
  const selectedMembership = user?.memberships?.find((m) => m.organizationId === selectedOrgId)
  const orgName = selectedMembership?.organizationName ?? ''
  // Whether this user may provision a number for the selected org — read from
  // that org's membership, not the global active org, so the no-number state can
  // distinguish "no number" from "you can't provision one here".
  const canProvisionNumber = selectedMembership?.permissions?.includes('numbers.manage') ?? false

  const [searchInput, setSearchInput] = useState('')
  const [rawPhoneNumber, setRawPhoneNumber] = useState<string | undefined>(undefined)
  const [contactName, setContactName] = useState('')
  const [contactLanguage, setContactLanguage] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [selectedResult, setSelectedResult] = useState<ContactSearchResult | null>(null)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)

  const debouncedSearch = useDebouncedValue(searchInput, 350)
  const showResults = debouncedSearch.trim().length >= 2

  const { data: searchResults, isLoading: isSearching } = useQuery(
    contactSearchQueries.search(debouncedSearch),
  )

  // Existing org contacts matching the same search term — the directory is a
  // first-class way to pick a recipient, alongside guest search and a raw number.
  const { data: contactsPage, isLoading: isSearchingContacts } = useQuery({
    ...contactQueries.list({
      filters: {
        ...(selectedOrgId ? { organization: selectedOrgId } : {}),
        search: debouncedSearch.trim(),
      },
    }),
    enabled: showResults && Boolean(selectedOrgId),
  })
  const directoryContacts = contactsPage?.results ?? []

  // Sending requires an active, WhatsApp-registered number on the org. Uses the
  // chat-facing sendable read (gated to chat.access, not numbers.manage) so an
  // ordinary chat member sees their org's number instead of a false empty state.
  // The endpoint already returns only active + WhatsApp-capable numbers.
  const { data: sendableNumbers, isLoading: isLoadingNumbers } = useQuery(
    numberQueries.sendable(selectedOrgId),
  )
  const hasActiveNumber = (sendableNumbers?.length ?? 0) > 0

  // Whether another of the user's orgs can send — drives the "switch orgs" hint
  // in the no-number state. One summary read, shared with the org selector.
  const { data: sendableSummary } = useQuery(numberQueries.sendableSummary())
  const anotherOrgCanSend = (sendableSummary ?? []).some(
    (row) => row.organization !== selectedOrgId && row.hasSendableNumber,
  )

  // The opening send goes out from one of the selected org's sendable numbers —
  // its default if marked, else the first — pinning the sending org to the chat
  // selection rather than a global default.
  const ourNumber =
    sendableNumbers?.find((number) => number.isDefault)?.phoneNumber ??
    sendableNumbers?.[0]?.phoneNumber

  const { data: templatesPage, isLoading: isLoadingTemplates } = useQuery(
    whatsappTemplateQueries.approved(selectedOrgId),
  )

  const recipientLanguage = contactLanguage || selectedContact?.language || null
  const templateGroups = useMemo(
    () => orderForRecipient(groupTemplatesByName(templatesPage?.results ?? []), recipientLanguage),
    [templatesPage, recipientLanguage],
  )
  const [templateName, setTemplateName] = useState<string | null>(null)
  // Any approved template can open a conversation; the default only leads the
  // list. The recipient's language is sent when the template is approved in it
  // (the server resolves the same way), else its first approved language.
  const templateGroup =
    templateGroups.find((group) => group.name === templateName) ?? templateGroups[0] ?? null
  const openingTemplate = templateGroup
    ? (recipientLanguage && templateGroup.byLanguage[recipientLanguage]) ||
      templateGroup.byLanguage[Object.keys(templateGroup.byLanguage).sort()[0]]
    : null

  const recipientName =
    contactName.trim() ||
    selectedContact?.displayName ||
    (selectedResult
      ? `${selectedResult.guest.firstName || ''} ${selectedResult.guest.lastName || ''}`.trim()
      : '') ||
    rawPhoneNumber ||
    ''

  // No conversation exists yet, so bindings resolve from the recipient being
  // picked. The server names a new contact after its number when no name is
  // given, so the contact name falls back the same way.
  const bindingContext = useMemo<BindingContext>(
    () => ({
      contactRef: {
        id: selectedContact?.id ?? '',
        displayName: recipientName || null,
        phoneNumber: rawPhoneNumber ?? null,
        language: recipientLanguage,
      },
      guestRef: selectedResult?.guest ?? null,
      reservationRef: selectedResult?.reservation ?? null,
    }),
    [selectedContact, recipientName, rawPhoneNumber, recipientLanguage, selectedResult],
  )
  const { bindings, promptedVars, overrides, setOverride, values, hasEveryValue, manualValues } =
    useTemplateSendValues(openingTemplate, bindingContext, orgName)
  const openingTemplateOptions = useMemo(
    () => ({
      openingTemplateName: openingTemplate?.name,
      openingTemplateLanguage: openingTemplate?.language,
      openingTemplateVariables: manualValues,
    }),
    [openingTemplate, manualValues],
  )
  const canSendOpening = Boolean(openingTemplate) && hasEveryValue

  const handleSelectResult = useCallback((result: ContactSearchResult) => {
    setSelectedResult(result)
    setSelectedContact(null)
    setValidationError(null)
    // Populate phone with guest's phone if available
    if (result.guest.phoneNumber) {
      setRawPhoneNumber(result.guest.phoneNumber)
    }
  }, [])

  const handleSelectContact = useCallback((contact: Contact) => {
    setSelectedContact(contact)
    setSelectedResult(null)
    setValidationError(null)
    setRawPhoneNumber(contact.phoneNumber)
    setContactName(contact.displayName ?? '')
    setContactLanguage(contact.language ?? '')
  }, [])

  const handleSubmitSelected = useCallback(() => {
    if (!selectedResult) return
    const phoneToUse = selectedResult.guest.phoneNumber ?? rawPhoneNumber
    if (!phoneToUse) {
      setValidationError(t('chat.noPhoneForContact'))
      return
    }
    setValidationError(null)
    onSubmit(phoneToUse, {
      reservationId: selectedResult.reservation?.id,
      ...openingTemplateOptions,
      ourNumber,
    })
  }, [selectedResult, rawPhoneNumber, openingTemplateOptions, ourNumber, onSubmit, t])

  const handleSubmitRaw = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!rawPhoneNumber) {
        setValidationError(t('chat.phoneRequired'))
        return
      }
      if (!isValidPhone(rawPhoneNumber)) {
        setValidationError(t('chat.invalidPhone'))
        return
      }
      setValidationError(null)
      onSubmit(rawPhoneNumber, {
        contactName: contactName.trim() || undefined,
        contactLanguage: contactLanguage || undefined,
        ...openingTemplateOptions,
        ourNumber,
      })
    },
    [rawPhoneNumber, contactName, contactLanguage, openingTemplateOptions, ourNumber, onSubmit, t],
  )

  // The opening send is logged raw for debugging but shown as plain copy only.
  useEffect(() => {
    if (error) console.error('WhatsApp opening send failed', error)
  }, [error])
  const showBalanceError = !validationError && isInsufficientBalanceError(error)
  // A plain message: the local validation string if any, else a generic send
  // failure (the balance case renders its own copy + top-up link below).
  const displayError =
    validationError ?? (error && !showBalanceError ? t('chat.failedToSend') : null)

  const header = (
    <div className="flex items-center justify-between border-b border-border px-4 py-3">
      <h2 className="text-base font-semibold text-text-primary">{t('chat.newConversation')}</h2>
      <Button
        type="button"
        variant="ghost"
        tone="neutral"
        iconOnly
        size="sm"
        shape="square"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
      </Button>
    </div>
  )

  // No active number means nothing can be sent. Name the org that can't send,
  // point at provisioning for that specific org (or tell non-admins to ask one),
  // and — if another of the user's orgs can send — nudge them to switch orgs,
  // rather than letting the team compose into a dead end.
  if (!isLoadingNumbers && !hasActiveNumber) {
    return (
      <div className="flex h-full flex-col border-r border-border bg-surface">
        {header}
        <div
          className="flex flex-1 flex-col items-start gap-3 p-4"
          data-testid="new-conversation-no-active-number"
        >
          <p className="text-sm font-medium text-text-primary">
            {t('chat.noActiveNumberForOrg', { org: orgName })}
          </p>
          {canProvisionNumber ? (
            <>
              <p className="text-sm text-text-secondary">{t('chat.noActiveNumberHint')}</p>
              <Link
                to={
                  selectedOrgId
                    ? `/organization-settings?organization=${selectedOrgId}`
                    : '/organization-settings'
                }
                className="text-sm font-medium text-primary underline hover:no-underline"
              >
                {t('chat.provisionNumber')}
              </Link>
            </>
          ) : (
            // The user can't provision here, so distinguish this from "no number".
            <p className="text-sm text-text-secondary">{t('chat.noActiveNumberNoPermission')}</p>
          )}
          {anotherOrgCanSend ? (
            <p
              className="text-sm text-text-secondary"
              data-testid="new-conversation-switch-org-hint"
            >
              {t('chat.switchOrgToSend')}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col border-r border-border bg-surface">
      {header}

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        {/* Search section */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-primary">
            {t('chat.searchByNameOrPhone')}
          </label>
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder={t('chat.contactNameOrPhone')}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                setSelectedResult(null)
                setSelectedContact(null)
              }}
              className="w-full rounded-md border bg-surface py-2 pl-9 pr-3 text-text-primary placeholder:font-thin placeholder:text-text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            />
          </div>
        </div>

        {/* Org contact directory results */}
        {showResults && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
              {t('chat.contactsSection')}
            </p>
            {isSearchingContacts ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : directoryContacts.length > 0 ? (
              <div
                className="divide-y divide-border rounded-md border border-border"
                data-testid="new-conversation-contact-results"
              >
                {directoryContacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleSelectContact(contact)}
                    className={cn([
                      'flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors',
                      selectedContact?.id === contact.id
                        ? 'bg-gray-100 text-text-primary'
                        : 'text-text-primary hover:bg-surface-hover',
                    ])}
                  >
                    <span className="text-sm font-medium">{contact.displayName}</span>
                    <span className="text-xs text-text-placeholder">{contact.phoneNumber}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="py-3 text-center text-sm text-text-tertiary">
                {t('chat.noContactsFound')}
              </p>
            )}
          </div>
        )}

        {/* Guest search results */}
        {showResults && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
              {t('chat.guestsSection')}
            </p>
            {isSearching ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="divide-y divide-border rounded-md border border-border">
                {searchResults.map((result, idx) => {
                  const guestName =
                    `${result.guest.firstName || ''} ${result.guest.lastName || ''}`.trim() ||
                    result.guest.phoneNumber ||
                    t('chat.unknown')
                  const reservationSummary = formatReservationSummary(result.reservation, t)
                  const isSelected = selectedResult?.guest.id === result.guest.id

                  return (
                    <button
                      key={result.guest.id ?? idx}
                      type="button"
                      onClick={() => handleSelectResult(result)}
                      className={cn([
                        'flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors',
                        isSelected
                          ? 'bg-gray-100 text-text-primary'
                          : 'text-text-primary hover:bg-surface-hover',
                      ])}
                    >
                      <span className="text-sm font-medium">{guestName}</span>
                      {result.guest.phoneNumber && (
                        <span className="text-xs text-text-placeholder">
                          {result.guest.phoneNumber}
                        </span>
                      )}
                      {reservationSummary && (
                        <span className="text-xs text-accent-600">
                          {t('chat.reservation')}: {reservationSummary}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="py-3 text-center text-sm text-text-tertiary">
                {t('chat.noGuestsFound')}
              </p>
            )}
          </div>
        )}

        {/* Submit selected guest result */}
        {selectedResult && (
          <div className="flex flex-col gap-2">
            <div className="rounded-md border border-border bg-surface px-3 py-2">
              <p className="text-sm font-medium text-primary-800">
                {`${selectedResult.guest.firstName || ''} ${
                  selectedResult.guest.lastName || ''
                }`.trim() || selectedResult.guest.phoneNumber}
              </p>
              {selectedResult.reservation && (
                <p className="text-xs text-primary-600">
                  {t('chat.reservation')}: {formatReservationSummary(selectedResult.reservation, t)}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmitSelected}
              isLoading={isLoading}
              disabled={isLoading || !canSendOpening}
              className="w-full"
            >
              {t('chat.startConversation')}
            </Button>
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-border" />
          <span className="text-xs text-text-tertiary">{t('chat.orEnterPhoneDirectly')}</span>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* Raw phone number entry */}
        <form onSubmit={handleSubmitRaw} className="flex flex-col gap-3">
          <PhoneNumberInput
            label={t('chat.phoneNumber')}
            placeholder="+573001234567"
            value={rawPhoneNumber}
            onChange={(v) => {
              setRawPhoneNumber(v)
              if (validationError) setValidationError(null)
              if (selectedResult) setSelectedResult(null)
            }}
            disabled={isLoading}
            data-testid="new-conversation-phone-input"
          />

          {/* Optional contact details — name + language are saved to the org
              contact directory in the same step as starting the conversation. */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="new-conversation-contact-name"
              className="text-sm font-medium text-primary"
            >
              {t('chat.contactNameLabel')}{' '}
              <span className="font-normal text-text-tertiary">{t('chat.optional')}</span>
            </label>
            <input
              id="new-conversation-contact-name"
              type="text"
              placeholder={t('chat.contactNamePlaceholder')}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              disabled={isLoading}
              data-testid="new-conversation-contact-name"
              className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary placeholder:font-thin placeholder:text-text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="new-conversation-contact-language"
              className="text-sm font-medium text-primary"
            >
              {t('chat.contactLanguageLabel')}{' '}
              <span className="font-normal text-text-tertiary">{t('chat.optional')}</span>
            </label>
            <select
              id="new-conversation-contact-language"
              value={contactLanguage}
              onChange={(e) => setContactLanguage(e.target.value)}
              disabled={isLoading}
              data-testid="new-conversation-contact-language"
              className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            >
              {CONTACT_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.value === '' ? t('chat.languageAuto') : opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Opening message. First contact is business-initiated, so WhatsApp
              only allows an approved template — there is no free-text box here.
              Free-form replies unlock once the customer answers. */}
          <div className="flex flex-col gap-1" data-testid="new-conversation-opening-message">
            <p className="text-sm font-medium text-primary">{t('chat.openingMessage')}</p>
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 py-2">
                <Spinner size="xs" />
              </div>
            ) : openingTemplate && templateGroup ? (
              <>
                <label htmlFor="new-conversation-template" className="sr-only">
                  {t('chat.templateLabel')}
                </label>
                <select
                  id="new-conversation-template"
                  value={templateGroup.name}
                  onChange={(e) => setTemplateName(e.target.value)}
                  disabled={isLoading}
                  data-testid="new-conversation-template-select"
                  className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
                >
                  {templateGroups.map((option) => (
                    <option key={option.name} value={option.name}>
                      {option.isDefault
                        ? `${option.name} (${t('chat.defaultTemplateLabel')})`
                        : option.name}
                    </option>
                  ))}
                </select>
                <TemplateVariablePrompts
                  promptedVars={promptedVars}
                  bindings={bindings}
                  overrides={overrides}
                  setOverride={setOverride}
                  disabled={isLoading}
                  testIdPrefix="new-conversation"
                />
                <div className="flex items-start gap-1.5">
                  <p
                    className="flex-1 rounded-md border border-border bg-surface-hover px-3 py-2 text-sm text-text-secondary"
                    data-testid="new-conversation-template-preview"
                  >
                    {renderBody(openingTemplate.body, values)}
                  </p>
                  <InfoHint
                    text={t('chat.freeTextAfterReply')}
                    className="mt-2.5"
                    data-testid="new-conversation-free-text-hint"
                  />
                </div>
              </>
            ) : (
              <NoApprovedTemplateNotice organization={selectedOrgId}>
                <div className="flex flex-col items-start gap-1" data-testid="no-approved-template">
                  <p className="text-sm text-error-600">{t('chat.noApprovedTemplate')}</p>
                  <Button
                    type="button"
                    variant="text"
                    tone="primary"
                    onClick={onReviewTemplates}
                    className="text-sm font-medium"
                  >
                    {t('chat.reviewTemplates')}
                  </Button>
                </div>
              </NoApprovedTemplateNotice>
            )}
          </div>

          {showBalanceError && (
            <div
              className="flex items-center gap-2 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-800"
              data-testid="new-conversation-insufficient-balance"
            >
              <ExclamationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('billing.insufficientBalance')}</span>
              <Link to="/billing" className="ml-auto font-medium underline hover:no-underline">
                {t('billing.topUp')}
              </Link>
            </div>
          )}

          {displayError && (
            <div className="flex items-center gap-1.5 text-sm text-error-600">
              <ExclamationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{displayError}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoading || !rawPhoneNumber || !canSendOpening}
            className="w-full"
          >
            {t('chat.startWithThisNumber')}
          </Button>
        </form>
      </div>
    </div>
  )
}
